import {
  createRootRouteWithContext,
  createRoute,
  lazyRouteComponent,
  Outlet,
  redirect,
  stripSearchParams,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import {
  getFilteredModelsQueryOptions,
  getFilteredProjectsQueryOptions,
} from '~/data/ai';
import { assetReparsingLoader } from '~/data/asset-reparsing';
import {
  getActivationRoutingDiagnosisQueryOptions,
  getCompaniesAdminQueryOptions,
  getCompanyAdminQueryOptions,
  getCompanyAliasAddressesQueryOptions,
} from '~/data/company';
import {
  getCompanyAccountQueryOptions,
  getCompanyAccountsAdminQueryOptions,
} from '~/data/company-account';
import {
  getDeliveryNoteAdminQueryOptions,
  getDeliveryNotesAdminQueryOptions,
} from '~/data/delivery';
import { ensureQueryDataIgnoringCancellation } from '~/data/ensureQueryDataIgnoringCancellation';
import { getFtpPollersQueryOptions } from '~/data/ftp-poller';
import { getInvoiceAiModelsSettingsQueryOptions } from '~/data/invoice-ai-model';
import {
  getInvoiceStopperRulesQueryOptions,
  getInvoiceWatermarksQueryOptions,
  getReprocessingInvoicesQueryOptions,
} from '~/data/invoice-reprocessing';
import { getSupplierRequestQueryOptions } from '~/data/supplier-request/useQuerySupplierRequest';
import { getSupplierRequestsQueryOptions } from '~/data/supplier-request/useQuerySupplierRequests';
import { getUnitMappingsQueryOptions } from '~/data/unit/useQueryUnitMappings';
import { getUsersAdminQueryOptions } from '~/data/user';

import { getFullFilterConfig } from '~/modules/delivery-notes/list/utils';

import { getSchemaDefaults } from '@vestigas/utils';
import { i18n } from '~/utils';

import { EmptyStateIllustration } from '~/assets/illustrations';

import { ErrorPage } from './components';
import { createProtectedRoute } from './routeProtection';
import { type RouterContext } from './types';
import {
  zodApiPollerSecretsSearchParamsSchema,
  zodCompaniesAdminQueryParams,
  zodCompanyAliasAddressesSearchParametersSchema,
  zodCompanySearchParametersSchema,
  zodConnectionsCompanySearchParametersSchema,
  zodConnectionsDataExchangeSearchParametersSchema,
  zodDeliveryNoteReparsingV2ListSearchParamsSchema,
  zodDeliveryNoteSearchParamsSchema,
  zodDeliveryNotesSearchParamsSchema,
  zodFilteredProjectsRouterSearchParamsSchema,
  zodFtpPollersSearchParamsSchema,
  zodInvoiceAiModelsSearchParametersSchema,
  zodInvoiceForwardingsListSearchParamsSchema,
  zodInvoiceReprocessingEditSearchParamsSchema,
  zodInvoiceReprocessingListSearchParamsSchema,
  zodInvoiceStoppersListSearchParamsSchema,
  zodUnitMappingsRouterSearchParamsSchema,
  zodSupplierRequestsListSearchParamsSchema,
} from './validations';

// Root route
export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: lazyRouteComponent(
    async () => import('~/ui/layouts/AppLayout'),
    'AppLayout',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.home.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.home.error.title, { ns: 'admin-webapp' })}
    />
  ),
  head: () => ({
    meta: [
      {
        name: 'description',
        content: i18n.t(($) => $.routes.app.metaDescription, {
          ns: 'admin-webapp',
        }),
      },
      { title: i18n.t(($) => $.routes.app.metaTitle, { ns: 'admin-webapp' }) },
    ],
  }),
  wrapInSuspense: true,
});

// Base route
const baseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  // Keeps the query so an OIDC callback (code, state) arriving at the root
  // survives the redirect until the AuthProvider has processed it.
  loader: () => redirect({ to: '/home', search: true }),
});

// Home route
export const homeRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(async () => import('../home'), 'Home'),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.home.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.home.error.title, { ns: 'admin-webapp' })}
    />
  ),
  getParentRoute: () => rootRoute,
  path: '/home',
});

// #region Invoice Reprocessing routes
export const invoiceReprocessingRootRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: () => <Outlet />,
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceReprocessing.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceReprocessing.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  path: '/invoice-reprocessing',
});

export const invoiceReprocessingListRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('../invoice-reprocessing/list'),
    'InvoiceReprocessingList',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceReprocessing.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceReprocessing.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => invoiceReprocessingRootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.invoiceReprocessingList.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  async loader({ context: { queryClient }, deps: { queryParameters } }) {
    return queryClient.ensureQueryData(
      getReprocessingInvoicesQueryOptions({ queryParameters }),
    );
  },
  loaderDeps({ search }) {
    return {
      queryParameters: {
        ...search,
        createdAfter:
          search.createdAfter && search.createdAfter instanceof Date
            ? search.createdAfter.toISOString()
            : undefined,
        createdBefore:
          search.createdBefore && search.createdBefore instanceof Date
            ? search.createdBefore?.toISOString()
            : undefined,
      },
    };
  },
  path: '/',
  search: {
    middlewares: [
      stripSearchParams(
        getSchemaDefaults(zodInvoiceReprocessingListSearchParamsSchema),
      ),
    ],
  },
  validateSearch: zodInvoiceReprocessingListSearchParamsSchema,
});

export const invoiceReprocessingEditRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('../invoice-reprocessing/edit'),
    'InvoiceReprocessingEdit',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceReprocessingEdit.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceReprocessingEdit.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => invoiceReprocessingRootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.invoiceReprocessingEdit.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  path: '$processId',
  validateSearch: zodValidator(zodInvoiceReprocessingEditSearchParamsSchema),
});
// #endregion

// #region Invoice Stopper routes
export const invoiceStoppersRouteRoot = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: () => <Outlet />,
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceStoppersList.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceStoppersList.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  async loader({ context: { queryClient } }) {
    return queryClient.ensureQueryData(getInvoiceWatermarksQueryOptions());
  },
  path: '/invoice-stoppers',
});

export const invoiceStoppersListRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/invoice-stopper/list'),
    'InvoiceStoppersList',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceStoppersList.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceStoppersList.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => invoiceStoppersRouteRoot,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.invoiceStoppersList.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  async loader({ context: { queryClient }, deps: { queryParameters } }) {
    return queryClient.ensureQueryData(
      getInvoiceStopperRulesQueryOptions({ queryParameters }),
    );
  },
  loaderDeps({ search }) {
    return { queryParameters: search };
  },
  path: '/',
  search: {
    middlewares: [
      stripSearchParams(
        getSchemaDefaults(zodInvoiceStoppersListSearchParamsSchema),
      ),
    ],
  },
  validateSearch: zodValidator(zodInvoiceStoppersListSearchParamsSchema),
});

export const invoiceStopperSingleRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/invoice-stopper/single/InvoiceStopper'),
    'InvoiceStopper',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceStopperSingle.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceStopperSingle.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => invoiceStoppersRouteRoot,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.invoiceStopperSingle.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  path: '$stopperRuleId',
});

export const invoiceStopperCreateRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/invoice-stopper/single'),
    'InvoiceStopperForm',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceStopperCreate.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceStopperCreate.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => invoiceStoppersRouteRoot,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.invoiceStopperCreate.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  path: '/create',
});

export const invoiceStopperEditRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/invoice-stopper/single'),
    'InvoiceStopperForm',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceStopperEdit.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceStopperEdit.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => invoiceStoppersRouteRoot,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.invoiceStopperEdit.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  path: '$stopperRuleId/edit',
});
// #endregion

// #region Invoice Forwarding routes
export const invoiceForwardingsRouteRoot = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin'],
  }),
  component: () => <Outlet />,
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceForwardingsList.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceForwardingsList.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  path: '/invoice-forwardings',
});

export const invoiceForwardingsListRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/invoice-forwarding/list'),
    'InvoiceForwardingList',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceForwardingsList.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceForwardingsList.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => invoiceForwardingsRouteRoot,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.invoiceForwardingsList.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  path: '/',
  search: {
    middlewares: [
      stripSearchParams(
        getSchemaDefaults(zodInvoiceForwardingsListSearchParamsSchema),
      ),
    ],
  },
  validateSearch: zodValidator(zodInvoiceForwardingsListSearchParamsSchema),
});

export const invoiceForwardingSingleRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/invoice-forwarding/single'),
    'InvoiceForwardingForm',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceForwardingSingle.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceForwardingSingle.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => invoiceForwardingsRouteRoot,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.invoiceForwardingSingle.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  path: '$ruleId',
  validateParams: zodValidator({
    ruleId: z.uuid(),
  }),
});

export const invoiceForwardingCreateRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/invoice-forwarding/single'),
    'InvoiceForwardingForm',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.invoiceForwardingCreate.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.invoiceForwardingCreate.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => invoiceForwardingsRouteRoot,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.invoiceForwardingCreate.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  path: '/create',
});
// #endregion

// AI Parser Review route
export const aiParserReviewRoute = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin', 'external_admin'],
  }),
  component: lazyRouteComponent(
    async () => import('../ai-parser-review'),
    'AIParserReview',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.aiParserReview.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.aiParserReview.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.aiParserReview.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  async loader({ context: { queryClient } }) {
    return assetReparsingLoader(queryClient, {
      limit: PAGINATION_PAGE_SIZE_DEFAULT,
      offset: 0,
      reparsingCategory: 'ai',
    });
  },
  path: '/ai-parser-review',
});

// #region Asset Reparsing routes
const createAssetReparsingRoute = (
  path: string,
  category: 'frontend' | 'backend',
  options: { head: () => { meta: Array<{ title: string }> } },
) =>
  createRoute({
    beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
    component: lazyRouteComponent(
      async () => import('../asset-reparsing'),
      'AssetReparsing',
    ),
    context: () => ({ reparsingCategory: category }),
    errorComponent: () => (
      <ErrorPage
        message={i18n.t(($) => $.routes.assetReparsing.error.message, {
          ns: 'admin-webapp',
        })}
        title={i18n.t(($) => $.routes.assetReparsing.error.title, {
          ns: 'admin-webapp',
        })}
      />
    ),
    getParentRoute: () => rootRoute,
    async loader({ context: { queryClient } }) {
      return assetReparsingLoader(queryClient, {
        limit: PAGINATION_PAGE_SIZE_DEFAULT,
        offset: 0,
        reparsingCategory: category,
      });
    },
    path,
    ...options,
  });

export const assetReparsingRoute = createAssetReparsingRoute(
  '/asset-reparsing',
  'frontend',
  {
    head: () => ({
      meta: [
        {
          title: i18n.t(($) => $.routes.assetReparsing.metaTitle, {
            ns: 'admin-webapp',
          }),
        },
      ],
    }),
  },
);
export const assetReparsingBackendRoute = createAssetReparsingRoute(
  '/asset-reparsing-backend',
  'backend',
  {
    head: () => ({
      meta: [
        {
          title: i18n.t(($) => $.routes.assetReparsingBackend.metaTitle, {
            ns: 'admin-webapp',
          }),
        },
      ],
    }),
  },
);

export const deliveryNoteReparsingV2Route = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin', 'external_admin'],
  }),
  component: lazyRouteComponent(
    async () => import('~/modules/delivery-note-reparsing-v2'),
    'DeliveryNoteReparsingV2',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.deliveryNoteReparsingV2.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.deliveryNoteReparsingV2.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.deliveryNoteReparsingV2.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  path: '/delivery-note-reparsing-v2',
  search: {
    middlewares: [
      stripSearchParams(
        getSchemaDefaults(zodDeliveryNoteReparsingV2ListSearchParamsSchema),
      ),
    ],
  },
  validateSearch: zodDeliveryNoteReparsingV2ListSearchParamsSchema,
});
// #endregion

// #region Company Alias Address routes
export const companyAliasAddressesListRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(
    async () =>
      import('~/modules/clients/companies/alias-addresses/list/AliasAddressesList'),
    'AliasAddressesList',
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [
      { title: i18n.t(($) => $.routes.companyAliasAddressesList.metaTitle) },
    ],
  }),
  async loader({ context: { queryClient }, deps: { queryParameters } }) {
    return queryClient.ensureQueryData(
      getCompanyAliasAddressesQueryOptions({ searchParams: queryParameters }),
    );
  },
  loaderDeps({ search }) {
    return { queryParameters: search };
  },
  path: '/company-alias-addresses',
  search: {
    middlewares: [
      stripSearchParams(
        getSchemaDefaults(zodCompanyAliasAddressesSearchParametersSchema),
      ),
    ],
  },
  validateSearch: zodValidator(zodCompanyAliasAddressesSearchParametersSchema),
});

export const companyAliasAddressCreateRoute = createRoute({
  component: lazyRouteComponent(
    async () =>
      import('~/modules/clients/companies/alias-addresses/single/AdminAliasAddressForm'),
    'AdminAliasAddressForm',
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [
      { title: i18n.t(($) => $.routes.companyAliasAddressCreate.metaTitle) },
    ],
  }),
  path: '/company-alias-addresses/create',
});
export const companyAliasAddressEditRoute = createRoute({
  component: lazyRouteComponent(
    async () =>
      import('~/modules/clients/companies/alias-addresses/single/AdminAliasAddressForm'),
    'AdminAliasAddressForm',
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [
      { title: i18n.t(($) => $.routes.companyAliasAddressEdit.metaTitle) },
    ],
  }),
  path: '/company-alias-addresses/$aliasAddressId/edit',
});

export const companyAliasAddressSingleRoute = createRoute({
  component: lazyRouteComponent(
    async () =>
      import('~/modules/clients/companies/alias-addresses/single/AliasAddress'),
    'AliasAddress',
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [
      { title: i18n.t(($) => $.routes.companyAliasAddressView.metaTitle) },
    ],
  }),
  path: '/company-alias-addresses/$aliasAddressId',
});
// #endregion

// Connections routes
export const connectionsRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: () => <Outlet />,
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.supplierConnections.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.supplierConnections.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  path: '/connections',
});

export const connectionsRouteRoot = createRoute({
  getParentRoute: () => connectionsRoute,
  path: '/',
  loader: () => redirect({ to: '/connections/data-exchange-companies' }),
});

export const dataExchangeCompaniesRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('../supplier-connections/data-exchange-companies'),
    'DataExchangeCompanies',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(
        ($) => $.routes.supplierConnectionsDataExchangeCompanies.error.message,
        { ns: 'admin-webapp' },
      )}
      title={i18n.t(
        ($) => $.routes.supplierConnectionsDataExchangeCompanies.error.title,
        { ns: 'admin-webapp' },
      )}
    />
  ),
  getParentRoute: () => connectionsRoute,
  path: '/data-exchange-companies',
  validateSearch: zodValidator(zodConnectionsCompanySearchParametersSchema),
});

export const dataExchangesRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('../supplier-connections/data-exchanges'),
    'DataExchanges',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(
        ($) => $.routes.supplierConnectionsDataExchanges.error.message,
        {
          ns: 'admin-webapp',
        },
      )}
      title={i18n.t(
        ($) => $.routes.supplierConnectionsDataExchanges.error.title,
        {
          ns: 'admin-webapp',
        },
      )}
    />
  ),
  getParentRoute: () => connectionsRoute,
  path: '/data-exchanges',
  validateSearch: zodValidator(
    zodConnectionsDataExchangeSearchParametersSchema,
  ),
});

// Endpoints route
export const endpointsRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(
    async () => import('../endpoints'),
    'Endpoints',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.endpoints.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.endpoints.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  path: '/endpoints',
});

// #region FTP Pollers routes
export const ftpPollersListRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(
    async () => import('../ftp-pollers/FtpPollersList'),
    'FtpPollersList',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.ftpPollers.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.ftpPollers.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.ftpPollers.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  loader({ context: { queryClient }, deps: { queryParameters } }) {
    const nextPageOffset =
      queryParameters.offset + PAGINATION_PAGE_SIZE_DEFAULT;

    void queryClient.prefetchQuery(
      getFtpPollersQueryOptions({ queryParameters }),
    );
    void queryClient.prefetchQuery(
      getFtpPollersQueryOptions({
        queryParameters: {
          ...queryParameters,
          offset: nextPageOffset,
        },
      }),
    );
  },
  loaderDeps({ search }) {
    return {
      queryParameters: {
        ...search,
        limit: PAGINATION_PAGE_SIZE_DEFAULT,
        offset: search.page * PAGINATION_PAGE_SIZE_DEFAULT,
        orderBy: search.sortBy,
        sort: search.sortDirection,
      },
    };
  },
  path: '/ftp-poller-secrets',
  search: {
    middlewares: [
      stripSearchParams(getSchemaDefaults(zodFtpPollersSearchParamsSchema)),
    ],
  },
  validateSearch: zodValidator(zodFtpPollersSearchParamsSchema),
});
export const ftpPollerEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ftp-poller-secrets/$ftpPollerId',
  component: lazyRouteComponent(
    async () => import('../ftp-pollers/edit/FtpPollerForm'),
    'FtpPollerForm',
  ),
});
export const ftpPollerCreateRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('../ftp-pollers/edit/FtpPollerForm'),
    'FtpPollerForm',
  ),
  getParentRoute: () => rootRoute,
  path: '/ftp-poller-secrets/create',
  validateSearch: zodValidator(z.object({ cloneFrom: z.guid().optional() })),
});
// #endregion

// #region API Poller Secrets routes
export const apiPollerSecretsRootRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: () => <Outlet />,
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.apiPollerSecrets.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.apiPollerSecrets.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  path: '/api-poller-secrets',
});

export const apiPollerSecretsListRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('../api-poller-secrets/ApiPollerSecretsList'),
    'ApiPollerSecretsList',
  ),
  getParentRoute: () => apiPollerSecretsRootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.apiPollerSecrets.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  path: '/',
  search: {
    middlewares: [
      stripSearchParams(
        getSchemaDefaults(zodApiPollerSecretsSearchParamsSchema),
      ),
    ],
  },
  validateSearch: zodValidator(zodApiPollerSecretsSearchParamsSchema),
});

export const apiPollerSecretCreateRoute = createRoute({
  getParentRoute: () => apiPollerSecretsRootRoute,
  path: '/create',
  component: lazyRouteComponent(
    async () => import('../api-poller-secrets/edit/ApiPollerSecretForm'),
    'ApiPollerSecretForm',
  ),
});

export const apiPollerSecretViewRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('../api-poller-secrets/view'),
    'ApiPollerSecret',
  ),
  getParentRoute: () => apiPollerSecretsRootRoute,
  params: zodValidator(z.object({ secretId: z.guid() })),
  path: '/$secretId',
});

export const apiPollerSecretEditRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('../api-poller-secrets/edit/ApiPollerSecretForm'),
    'ApiPollerSecretForm',
  ),
  getParentRoute: () => apiPollerSecretsRootRoute,
  params: zodValidator(z.object({ secretId: z.uuid() })),
  path: '/$secretId/edit',
});
// #endregion

// #region User routes
export const usersListRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(
    async () => import('~/modules/clients/users/list/Users'),
    'Users',
  ),
  getParentRoute: () => rootRoute,
  async loader({ context: { queryClient } }) {
    return queryClient.ensureQueryData(
      getUsersAdminQueryOptions({
        queryParameters: { limit: PAGINATION_PAGE_SIZE_DEFAULT },
      }),
    );
  },
  path: '/users',
});

export const userCreateRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(
    async () => import('~/modules/clients/users/single/AdminUserForm'),
    'AdminUserForm',
  ),
  getParentRoute: () => rootRoute,
  path: '/users/create',
  validateSearch: zodValidator(
    z.object({ companyAccountId: z.uuid().optional() }),
  ),
});

export const userSingleRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(
    async () => import('~/modules/clients/users/single/User'),
    'User',
  ),
  getParentRoute: () => rootRoute,
  path: '/users/$userId',
  validateSearch: zodValidator(
    z.object({ tab: z.string().optional(), view: z.string().optional() }),
  ),
});
// #endregion

// #region Company Account routes
export const companyAccountsListRoute = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin', 'external_admin'],
  }),
  component: lazyRouteComponent(
    async () =>
      import('~/modules/clients/companyAccounts/list/CompanyAccountsList'),
    'CompanyAccountsList',
  ),
  getParentRoute: () => rootRoute,
  async loader({ context: { queryClient } }) {
    return queryClient.ensureQueryData(
      getCompanyAccountsAdminQueryOptions({
        queryParameters: { limit: PAGINATION_PAGE_SIZE_DEFAULT },
      }),
    );
  },
  path: '/company-accounts',
});

export const companyAccountCreateRoute = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin', 'external_admin'],
  }),
  component: lazyRouteComponent(
    async () =>
      import('~/modules/clients/companyAccounts/single/AdminCompanyAccountForm'),
    'AdminCompanyAccountForm',
  ),
  getParentRoute: () => rootRoute,
  path: '/company-accounts/create',
});

export const companyAccountSingleRoute = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin', 'external_admin'],
  }),
  component: lazyRouteComponent(
    async () =>
      import('~/modules/clients/companyAccounts/single/CompanyAccount'),
    'CompanyAccount',
  ),
  getParentRoute: () => rootRoute,
  async loader({ context: { queryClient }, params: { companyAccountId } }) {
    return queryClient.fetchQuery(
      getCompanyAccountQueryOptions({ companyAccountId }),
    );
  },
  path: '/company-accounts/$companyAccountId',
  validateSearch: zodValidator(z.object({ view: z.string().optional() })),
});
// #endregion

// #region Company routes
export const companiesListRoute = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin', 'external_admin'],
  }),
  component: lazyRouteComponent(
    async () => import('~/modules/clients/companies/list/Companies'),
    'Companies',
  ),
  getParentRoute: () => rootRoute,
  async loader({ context: { queryClient } }) {
    return queryClient.ensureQueryData(
      getCompaniesAdminQueryOptions({
        queryParameters: { limit: PAGINATION_PAGE_SIZE_DEFAULT },
      }),
    );
  },
  path: '/companies',
  search: {
    middlewares: [
      stripSearchParams(getSchemaDefaults(zodCompaniesAdminQueryParams)),
    ],
  },
  validateSearch: zodValidator(zodCompaniesAdminQueryParams),
});

export const companyCreateRoute = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin', 'external_admin'],
  }),
  component: lazyRouteComponent(
    async () => import('~/modules/clients/companies/single/AdminCompanyForm'),
    'AdminCompanyForm',
  ),
  getParentRoute: () => rootRoute,
  path: '/companies/create',
  validateSearch: zodValidator(
    z.object({ companyAccountId: z.uuid().optional() }),
  ),
});

export const companySingleRoute = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin', 'external_admin'],
  }),
  component: lazyRouteComponent(
    async () => import('~/modules/clients/companies/single/Company'),
    'Company',
  ),
  getParentRoute: () => rootRoute,
  async loader({ context: { queryClient }, params: { companyId } }) {
    void queryClient.prefetchQuery(getCompanyAdminQueryOptions({ companyId }));
    void queryClient.prefetchQuery(
      getActivationRoutingDiagnosisQueryOptions({ companyId }),
    );
  },
  path: '/companies/$companyId',
  validateSearch: zodValidator(zodCompanySearchParametersSchema),
});
// #endregion

// #region Delivery Notes routes
export const deliveryNotesRootRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: () => <Outlet />,
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.deliveryNotes.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.deliveryNotes.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  path: '/delivery-notes',
});

export const deliveryNotesListRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/delivery-notes/list/DeliveryNotesList'),
    'DeliveryNotesList',
  ),
  getParentRoute: () => deliveryNotesRootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.deliveryNotes.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  async loader({ context: { queryClient }, deps: { queryParameters } }) {
    return queryClient.ensureQueryData(
      getDeliveryNotesAdminQueryOptions({ queryParameters }),
    );
  },
  loaderDeps({
    search: {
      dateRangeLabel,
      filterConfig,
      searchString,
      includeChains,
      modifiedAfter,
      page,
      orderBy,
      sortOrder,
    },
  }) {
    const queryParameters = {
      filterConfig: getFullFilterConfig({
        dateRangeLabel,
        filterConfig,
        searchString,
      }),
      includeChains,
      limit: PAGINATION_PAGE_SIZE_DEFAULT,
      modifiedAfter,
      offset: page * PAGINATION_PAGE_SIZE_DEFAULT,
      orderBy,
      sortOrder,
    };

    return { queryParameters };
  },
  path: '/',
  search: {
    middlewares: [
      stripSearchParams(getSchemaDefaults(zodDeliveryNotesSearchParamsSchema)),
    ],
  },
  validateSearch: zodValidator(zodDeliveryNotesSearchParamsSchema),
});

export const deliveryNotesViewRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/delivery-notes/view/DeliveryNote'),
    'DeliveryNote',
  ),
  getParentRoute: () => deliveryNotesRootRoute,
  head: ({ params }) => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.deliveryNoteView.metaTitle, {
          ns: 'admin-webapp',
          deliveryNoteId: params.deliveryNoteId,
        }),
      },
    ],
  }),
  async loader({ context: { queryClient }, params: { deliveryNoteId } }) {
    return queryClient.ensureQueryData(
      getDeliveryNoteAdminQueryOptions({ deliveryNoteId }),
    );
  },
  params: zodValidator(z.object({ deliveryNoteId: z.uuid() })),
  path: '/$deliveryNoteId',
  search: {
    middlewares: [
      stripSearchParams(getSchemaDefaults(zodDeliveryNoteSearchParamsSchema)),
    ],
  },
  validateSearch: zodValidator(zodDeliveryNoteSearchParamsSchema),
});

export const deliveryNoteDeletionRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(
    async () =>
      import('~/modules/delivery-notes/deletion/DeliveryNoteDeletion'),
    'DeliveryNoteDeletion',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.deliveryNoteDeletion.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.deliveryNoteDeletion.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.deliveryNoteDeletion.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  path: '/delivery-note-deletion',
});
// #endregion

// #region Invoice AI Model routes
export const invoiceAiModelsListRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(
    async () => import('~/modules/invoice-ai-models/list/InvoiceAiModelsList'),
    'InvoiceAiModelsList',
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [{ title: i18n.t(($) => $.routes.invoiceAiModelsList.metaTitle) }],
  }),
  async loader({ context: { queryClient }, deps: { queryParameters } }) {
    return queryClient.ensureQueryData(
      getInvoiceAiModelsSettingsQueryOptions({ queryParameters }),
    );
  },
  loaderDeps({ search }) {
    return { queryParameters: search };
  },
  path: '/invoice-ai-models',
  search: {
    middlewares: [
      stripSearchParams(
        getSchemaDefaults(zodInvoiceAiModelsSearchParametersSchema),
      ),
    ],
  },
  validateSearch: zodValidator(zodInvoiceAiModelsSearchParametersSchema),
});

export const invoiceAiModelCreateRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/invoice-ai-models/single/InvoiceAiModelForm'),
    'InvoiceAiModelForm',
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [{ title: i18n.t(($) => $.routes.invoiceAiModelsCreate.metaTitle) }],
  }),
  path: '/invoice-ai-models/create',
});
export const invoiceAiModelEditRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/invoice-ai-models/single/InvoiceAiModelForm'),
    'InvoiceAiModelForm',
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [{ title: i18n.t(($) => $.routes.invoiceAiModelsEdit.metaTitle) }],
  }),
  path: '/invoice-ai-models/$invoiceAiModelSettingsId/edit',
});

export const invoiceAiModelSingleRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/invoice-ai-models/single/InvoiceAiModel'),
    'InvoiceAiModel',
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [{ title: i18n.t(($) => $.routes.invoiceAiModelsView.metaTitle) }],
  }),
  path: '/invoice-ai-models/$invoiceAiModelSettingsId',
});
// #endregion

// #region AI Projects routes
export const aiProjectsRootRoute = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin', 'external_admin'],
  }),
  component: () => <Outlet />,
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.aiProjects.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.aiProjects.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  path: '/ai',
});

export const aiProjectsListRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/ai-projects/list/AiProjectsList'),
    'AiProjectsList',
  ),
  getParentRoute: () => aiProjectsRootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.aiProjectsList.metaTitle),
      },
    ],
  }),
  async loader({ context: { queryClient }, deps: { queryParameters } }) {
    return queryClient.ensureQueryData(
      getFilteredProjectsQueryOptions({
        queryParameters: {
          filterType: queryParameters.filterType,
        },
      }),
    );
  },
  loaderDeps({ search }) {
    return { queryParameters: search };
  },
  path: '/projects',
  search: {
    middlewares: [
      stripSearchParams(
        getSchemaDefaults(zodFilteredProjectsRouterSearchParamsSchema),
      ),
    ],
  },
  validateSearch: zodValidator(zodFilteredProjectsRouterSearchParamsSchema),
});

export const aiModelEvaluationRoute = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin', 'external_admin'],
  }),
  component: lazyRouteComponent(
    async () =>
      import('~/modules/ai-projects/model-evaluation/AiModelEvaluation'),
    'AiModelEvaluation',
  ),
  getParentRoute: () => aiProjectsRootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.aiModelEvaluation.metaTitle),
      },
    ],
  }),
  async loader({ context: { queryClient } }) {
    return queryClient.ensureQueryData(
      getFilteredModelsQueryOptions({
        queryParameters: {
          filterType: 'all',
          forceRefresh: false,
          platform: 'di', // Preload only the default platform (di) to avoid fetching all models upfront
        },
      }),
    );
  },
  path: '/model-evaluation',
});

export const aiLabelEvaluationRoute = createRoute({
  beforeLoad: createProtectedRoute({
    permissionLevel: ['admin', 'external_admin'],
  }),
  component: lazyRouteComponent(
    async () =>
      import('~/modules/ai-projects/label-evaluation/AiLabelEvaluation'),
    'AiLabelEvaluation',
  ),
  getParentRoute: () => aiProjectsRootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.aiLabelEvaluation.metaTitle),
      },
    ],
  }),
  async loader({ context: { queryClient } }) {
    return queryClient.ensureQueryData(
      getFilteredProjectsQueryOptions({
        queryParameters: {
          filterType: 'all',
          forceRefresh: false,
        },
      }),
    );
  },
  path: '/label-evaluation',
});
// #endregion

// #region Overlay Template routes
export const overlayTemplateSingleRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(
    async () => import('~/modules/overlay-settings/single/OverlaySetting.tsx'),
    'OverlaySetting',
  ),
  getParentRoute: () => rootRoute,
  params: zodValidator(z.object({ overlaySettingId: z.uuid() })),
  path: '/overlay-setting/$overlaySettingId',
});
// #endregion

// #region PDF Templates routes
export const pdfTemplatesRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(
    async () => import('../pdf-templates'),
    'PdfTemplates',
  ),
  errorComponent: () => (
    <ErrorPage
      message={i18n.t(($) => $.routes.pdfTemplates.error.message, {
        ns: 'admin-webapp',
      })}
      title={i18n.t(($) => $.routes.pdfTemplates.error.title, {
        ns: 'admin-webapp',
      })}
    />
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.pdfTemplates.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  path: '/pdf-templates',
});
// #endregion

// #region Supplier Requests routes
export const supplierRequestsRouteRoot = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: () => <Outlet />,
  getParentRoute: () => rootRoute,
  path: '/supplier-requests',
});

export const supplierRequestsListRoute = createRoute({
  component: lazyRouteComponent(
    async () => import('~/modules/supplier-requests/list'),
    'SupplierRequests',
  ),
  getParentRoute: () => supplierRequestsRouteRoot,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.supplierRequests.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  async loader({ context: { queryClient }, deps: { queryParameters } }) {
    return ensureQueryDataIgnoringCancellation(
      queryClient,
      getSupplierRequestsQueryOptions({
        queryParameters,
      }),
    );
  },
  loaderDeps({
    search: {
      requestingCompanyAccountId,
      requestingCompanyId,
      searchString,
      state,
    },
  }) {
    return {
      queryParameters: {
        requestingCompanyAccountId,
        requestingCompanyId,
        searchString,
        state,
      },
    };
  },
  path: '/',
  search: {
    middlewares: [
      stripSearchParams(
        getSchemaDefaults(zodSupplierRequestsListSearchParamsSchema),
      ),
    ],
  },
  validateSearch: zodValidator(zodSupplierRequestsListSearchParamsSchema),
});

export const supplierRequestSingleRoute = createRoute({
  component: lazyRouteComponent(
    async () =>
      import('~/modules/supplier-requests/single/SupplierRequestDetails'),
    'SupplierRequestDetails',
  ),
  getParentRoute: () => supplierRequestsRouteRoot,
  head: () => ({
    meta: [
      {
        title: i18n.t(($) => $.routes.supplierRequestSingle.metaTitle, {
          ns: 'admin-webapp',
        }),
      },
    ],
  }),
  async loader({ context: { queryClient }, params: { reqId } }) {
    return ensureQueryDataIgnoringCancellation(
      queryClient,
      getSupplierRequestQueryOptions({ reqId }),
    );
  },
  path: '$reqId',
});
// #endregion

// #region Unit Mapping routes
export const unitMappingsListRoute = createRoute({
  beforeLoad: createProtectedRoute({ permissionLevel: ['admin'] }),
  component: lazyRouteComponent(
    async () => import('~/modules/units/list/UnitMappingsList'),
    'UnitMappingsList',
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [{ title: i18n.t(($) => $.routes.unitMappingsList.metaTitle) }],
  }),
  async loader({ context: { queryClient }, deps: { queryParameters } }) {
    // Don't send full query parameters - we load all of the data and then filter client-side
    const coreQueryParameters = {
      limit: queryParameters.limit,
      offset: queryParameters.offset,
    };

    return queryClient.ensureQueryData(
      getUnitMappingsQueryOptions({ queryParameters: coreQueryParameters }),
    );
  },
  loaderDeps({ search }) {
    return { queryParameters: search };
  },
  path: '/unit-mappings',
  search: {
    middlewares: [
      stripSearchParams(
        getSchemaDefaults(zodUnitMappingsRouterSearchParamsSchema),
      ),
    ],
  },
  validateSearch: zodValidator(zodUnitMappingsRouterSearchParamsSchema),
});
// #endregion

// Unauthorized route - destination for users without permission to access a route.
// Intentionally unprotected to avoid recursive redirects.
export const unauthorizedRoute = createRoute({
  component: () => (
    <ErrorPage
      illustration={<EmptyStateIllustration className="h-64" />}
      message={i18n.t(($) => $.error401.message, { ns: 'admin-webapp' })}
      title={i18n.t(($) => $.error401.title, { ns: 'admin-webapp' })}
    />
  ),
  getParentRoute: () => rootRoute,
  head: () => ({
    meta: [{ title: i18n.t(($) => $.error401.title, { ns: 'admin-webapp' }) }],
  }),
  path: '/unauthorized',
});

// Route tree
export const routeTree = rootRoute.addChildren([
  aiProjectsRootRoute.addChildren([
    aiLabelEvaluationRoute,
    aiModelEvaluationRoute,
    aiProjectsListRoute,
  ]),
  aiParserReviewRoute,
  apiPollerSecretsRootRoute.addChildren([
    apiPollerSecretsListRoute,
    apiPollerSecretCreateRoute,
    apiPollerSecretEditRoute,
    apiPollerSecretViewRoute,
  ]),
  assetReparsingBackendRoute,
  assetReparsingRoute,
  baseRoute,
  companiesListRoute,
  companyAccountCreateRoute,
  companyAccountSingleRoute,
  companyAccountsListRoute,
  companyAliasAddressesListRoute.addChildren([
    companyAliasAddressCreateRoute,
    companyAliasAddressEditRoute,
    companyAliasAddressSingleRoute,
  ]),
  companyCreateRoute,
  companySingleRoute,
  connectionsRoute.addChildren([
    connectionsRouteRoot,
    dataExchangeCompaniesRoute,
    dataExchangesRoute,
  ]),
  deliveryNoteReparsingV2Route,
  deliveryNoteDeletionRoute,
  deliveryNotesRootRoute.addChildren([
    deliveryNotesListRoute,
    deliveryNotesViewRoute,
  ]),
  endpointsRoute,
  ftpPollersListRoute.addChildren([ftpPollerEditRoute, ftpPollerCreateRoute]),
  homeRoute,
  invoiceAiModelsListRoute.addChildren([
    invoiceAiModelCreateRoute,
    invoiceAiModelEditRoute,
    invoiceAiModelSingleRoute,
  ]),
  invoiceReprocessingRootRoute.addChildren([
    invoiceReprocessingListRoute,
    invoiceReprocessingEditRoute,
  ]),
  invoiceStoppersRouteRoot.addChildren([
    invoiceStoppersListRoute,
    invoiceStopperCreateRoute,
    invoiceStopperEditRoute,
    invoiceStopperSingleRoute,
  ]),
  invoiceForwardingsRouteRoot.addChildren([
    invoiceForwardingsListRoute,
    invoiceForwardingCreateRoute,
    invoiceForwardingSingleRoute,
  ]),
  overlayTemplateSingleRoute,
  pdfTemplatesRoute,
  supplierRequestsRouteRoot.addChildren([
    supplierRequestsListRoute,
    supplierRequestSingleRoute,
  ]),
  unauthorizedRoute,
  unitMappingsListRoute,
  userCreateRoute,
  userSingleRoute,
  usersListRoute,
]);
